import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAdminMessageDetail, useAdminMessageStats } from '@/features/messages/hooks/useMessages'
import { format } from 'date-fns'
import { ko } from 'date-fns/locale'

export default function AdminMessageDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation(['admin', 'messages'])
  const messageId = Number(id)

  const { data: message, isLoading } = useAdminMessageDetail(messageId)
  const { data: stats } = useAdminMessageStats(messageId)

  if (isLoading) {
    return <div className="p-6 text-center">{t('messages:detail.loading')}</div>
  }

  if (!message) {
    return <div className="p-6 text-center">{t('messages:detail.notFound')}</div>
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button
        onClick={() => navigate('/admin/messages')}
        className="text-blue-600 hover:underline mb-4"
      >
        {t('messages:detail.backToList')}
      </button>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h1 className="text-xl font-bold mb-2">{message.title}</h1>
        <p className="text-sm text-gray-500 mb-4">
          {format(new Date(message.createdAt), 'yyyy-MM-dd HH:mm', { locale: ko })}
        </p>
        <div className="prose max-w-none">
          <p className="whitespace-pre-wrap">{message.content}</p>
        </div>
      </div>

      {stats && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="font-bold mb-4">{t('messages:detail.readStatus')}</h2>
          <div className="flex gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{stats.total}</p>
              <p className="text-sm text-gray-500">{t('messages:detail.total')}</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{stats.read}</p>
              <p className="text-sm text-gray-500">{t('messages:detail.read')}</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-400">{stats.unread}</p>
              <p className="text-sm text-gray-500">{t('messages:detail.unread')}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <h2 className="font-bold p-4 border-b">{t('messages:detail.recipientList')}</h2>
        <div className="divide-y">
          {message.recipients.map((r) => (
            <div key={r.userId} className="p-4 flex justify-between items-center">
              <div>
                <p className="font-medium">{r.userName ?? t('messages:detail.unknownUser')}</p>
                <p className="text-sm text-gray-500">{r.departmentName ?? '-'}</p>
              </div>
              <div className="text-right">
                {r.isRead ? (
                  <span className="text-green-600 text-sm">
                    ✓ {format(new Date(r.readAt!), 'MM/dd HH:mm')}
                  </span>
                ) : (
                  <span className="text-gray-400 text-sm">{t('messages:detail.notRead')}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
